import { useState, useEffect } from 'react';
import { useReadContracts, useChainId } from 'wagmi';
import { formatUnits } from 'viem';
import { Link } from 'react-router';
import { Users, Lock, Coins, Layers, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Button } from '@/shared/ui/button';
import { contracts, galactica, galacticaCassiopeia } from '@/shared/config';
import { useContractSettings } from '@/shared/hooks/useContractSettings';
import { orpc } from '@/shared/api/client';
import { SystemHealthGraph } from '@/features/system-health';

// Minimal ABI for reading contract data
const votingEscrowAbi = [
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const;

const indexPoolAbi = [
  {
    name: 'totalSupply',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
] as const;

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  isLoading,
}: {
  title: string;
  value: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  isLoading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-[#807f7e]">{title}</CardTitle>
        <Icon className="w-5 h-5 text-[#807f7e]" />
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <p className="text-2xl font-bold text-[#2b2b2b]">{value}</p>
        )}
        <p className="text-xs text-[#807f7e] mt-1">{description}</p>
      </CardContent>
    </Card>
  );
}

export function DashboardPage() {
  const chainId = useChainId();
  const currentChain = chainId === galactica.id ? galactica.id : chainId;

  const votingEscrowAddress = contracts.votingEscrow[currentChain as keyof typeof contracts.votingEscrow];
  const indexPoolAddress = contracts.indexPool[currentChain as keyof typeof contracts.indexPool];
  const gubiAddress = contracts.gubi[currentChain as keyof typeof contracts.gubi];
  const { settings: contractSettings } = useContractSettings();
  const rewardDistributorAddress = contractSettings.rewardDistributor;
  const gnetRewardDistributorAddress = contractSettings.gnetRewardDistributor;
  const currentChainConfig = chainId === galactica.id ? galactica : galacticaCassiopeia;
  const explorerUrl = currentChainConfig.blockExplorers?.default.url || 'https://explorer.galactica.com';

  // Fetch stats from oRPC
  const [stats, setStats] = useState<{ totalUsers: number } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch pool data from REST API
  const [poolData, setPoolData] = useState<{ totalWorthUSD: number } | null>(null);
  const [poolLoading, setPoolLoading] = useState(true);

  useEffect(() => {
    orpc.stats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setStatsLoading(false));

    fetch(`/api/pool?chainId=${chainId}`)
      .then(res => {
        if (!res.ok) throw new Error(`Pool API error: ${res.status}`);
        return res.json();
      })
      .then(data => {
        console.log('[Dashboard] Pool data:', data);
        if (data.error) throw new Error(data.error);
        setPoolData(data);
      })
      .catch(err => console.error('[Dashboard] Pool fetch error:', err))
      .finally(() => setPoolLoading(false));
  }, [chainId]);

  const { data, isLoading } = useReadContracts({
    contracts: [
      {
        address: votingEscrowAddress,
        abi: votingEscrowAbi,
        functionName: 'totalSupply',
      },
      {
        address: gubiAddress,
        abi: [{ name: 'totalSupply', type: 'function', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] }] as const,
        functionName: 'totalSupply',
      },
    ],
    query: { enabled: !!votingEscrowAddress && !!gubiAddress },
  });

  const veGNETSupply = data?.[0]?.result;
  const gubiSupply = data?.[1]?.result;

  const formatLargeNumber = (value: bigint | undefined, decimals = 18) => {
    if (!value) return '0';
    const num = Number(formatUnits(value, decimals));
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
    return num.toFixed(2);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2b2b2b]">Dashboard</h1>
        <p className="text-[#807f7e]">Overview of the GUBI distribution system</p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total veGNET"
          value={formatLargeNumber(veGNETSupply)}
          description="Locked in Voting Escrow"
          icon={Lock}
          isLoading={isLoading}
        />
        <StatCard
          title="gUBI Supply"
          value={formatLargeNumber(gubiSupply)}
          description="Total gUBI minted"
          icon={Coins}
          isLoading={isLoading}
        />
        <StatCard
          title="Active Users"
          value={stats?.totalUsers?.toLocaleString() ?? '—'}
          description="With UBI reputation"
          icon={Users}
          isLoading={statsLoading}
        />
        <StatCard
          title="Index Pool"
          value={poolData?.totalWorthUSD ? `$${poolData.totalWorthUSD.toFixed(2)}` : '—'}
          description="Total value locked"
          icon={Layers}
          isLoading={poolLoading}
        />
      </div>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle>System Health</CardTitle>
          <CardDescription>Real-time status of all system components</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <SystemHealthGraph />
        </CardContent>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Users & Rankings</CardTitle>
            <CardDescription>View all users with UBI reputation</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/users">
              <Button className="gap-2">
                View Users
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Contract Status</CardTitle>
            <CardDescription>Smart contract information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-[#807f7e]">gUBI Token</span>
              {gubiAddress ? (
                <a
                  href={`${explorerUrl}/address/${gubiAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[#f26f56] hover:underline text-xs"
                >
                  {gubiAddress.slice(0, 6)}...{gubiAddress.slice(-4)}
                </a>
              ) : (
                <span className="text-[#807f7e] text-xs">Not configured</span>
              )}
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#807f7e]">Voting Escrow</span>
              <a
                href={`${explorerUrl}/address/${votingEscrowAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[#f26f56] hover:underline text-xs"
              >
                {votingEscrowAddress.slice(0, 6)}...{votingEscrowAddress.slice(-4)}
              </a>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#807f7e]">Index Pool</span>
              <a
                href={`${explorerUrl}/address/${indexPoolAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[#f26f56] hover:underline text-xs"
              >
                {indexPoolAddress.slice(0, 6)}...{indexPoolAddress.slice(-4)}
              </a>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#807f7e]">gUBI Distributor</span>
              {rewardDistributorAddress ? (
                <a
                  href={`${explorerUrl}/address/${rewardDistributorAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[#f26f56] hover:underline text-xs"
                >
                  {rewardDistributorAddress.slice(0, 6)}...{rewardDistributorAddress.slice(-4)}
                </a>
              ) : (
                <span className="text-[#807f7e] text-xs">Not configured</span>
              )}
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[#807f7e]">GNET Distributor</span>
              {gnetRewardDistributorAddress ? (
                <a
                  href={`${explorerUrl}/address/${gnetRewardDistributorAddress}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[#f26f56] hover:underline text-xs"
                >
                  {gnetRewardDistributorAddress.slice(0, 6)}...{gnetRewardDistributorAddress.slice(-4)}
                </a>
              ) : (
                <span className="text-[#807f7e] text-xs">Not configured</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
